/**
 * Fetching a brand's typeface, once, when a brand asks for one (2026-09-30).
 *
 * index.html loads Cormorant Garamond and Inter because every page needs them.
 * The rest of the catalog is downloaded only by the firms that chose it —
 * putting five families in the boot HTML would slow every page down for
 * everyone so that one firm's header could be set in Jost.
 *
 * Idempotent and safe to call on every render: a family already requested is
 * not requested twice, and a key that needs nothing does nothing.
 */

import { brandFontHref } from '@/lib/brand-type';

const requested = new Set<string>();

export function ensureBrandFont(key: string | null | undefined, doc: Document = document): void {
  const href = brandFontHref(key);
  if (!href || requested.has(href)) return;
  requested.add(href);
  // An existing tag means another surface on this page already asked — the
  // portal header and the app shell both do. Compared by VALUE rather than
  // with an attribute selector: these URLs carry `;` and `@` (a weight list
  // like `wght@300;400;500`), which a selector will not match unescaped.
  const already = [...doc.head.querySelectorAll('link')]
    .some((l) => l.getAttribute('href') === href);
  if (already) return;
  const link = doc.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  // A font that fails to arrive must not take the page's branding with it:
  // every stack in the catalog ends in a fallback of the same category.
  link.addEventListener('error', () => { requested.delete(href); });
  doc.head.append(link);
}

/** Tests only: forget what has been asked for. */
export function resetBrandFontLoader(): void {
  requested.clear();
}
